import { Button, Card, NumberInput } from '@sravni/react-design-system';
import type { ColSpanAdaptive } from '@sravni/react-design-system/dist/types/components/Grid/types/Col.types';
import { Col, Row } from '@sravni/react-design-system/lib/Grid';
import { useIsMobile } from '@sravni/react-utils';
import type { FocusEventHandler } from 'react';
import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { AMOUNTS } from 'constants/amounts';
import { CURRENCY_CHAR } from 'constants/currency';

import { useAppSelector } from 'shared/lib';
import { useSyncFormFieldsToQueryParams } from 'shared/lib/useSyncFormFieldsToQueryParams';

import { selectCreditPurposeInfoData, setCreditPurposeInfo } from 'entities/creditForm';
import { selectVitrinsOffersCount } from 'entities/vitrins/model/vitrins.selectors';

import { applyCreditCardsFilterThunk } from '../lib/applyCreditCardsFilterThunk';
import { VitrinsCreditCardsFiltersFormTexts } from '../VitrinsCreditCardsFiltersForm.texts';

import styles from './VitrinsCreditCardsFiltersForm.module.scss';

const colSpanConfig: ColSpanAdaptive = {
  mobile: 12,
  tablet: 12,
  desktop: 6,
};

type Props = {
  onButtonClick?: (amount: string) => void;
};

export const VitrinsCreditCardsFiltersForm: FC<Props> = (props) => {
  const { onButtonClick } = props;
  const isMobile = useIsMobile();
  const vitrinsOffersCount = useAppSelector(selectVitrinsOffersCount);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const creditPurposeFormData = useAppSelector(selectCreditPurposeInfoData);

  useSyncFormFieldsToQueryParams({
    amount: creditPurposeFormData?.amount,
    period: creditPurposeFormData?.period?.value,
    purpose: creditPurposeFormData?.purpose?.value,
  });

  const updateCreditLimitFilter = useCallback(
    async (value: string, callback?: (amount: string) => void) => {
      setIsLoading(true);
      dispatch(
        setCreditPurposeInfo({
          data: {
            ...creditPurposeFormData,
            amount: value.toString(),
          },
        }),
      );
      dispatch(
        applyCreditCardsFilterThunk({
          alias: 'amount',
          value: Number(value),
          callback: () => {
            setIsLoading(false);
            callback?.(value);
          },
        }),
      );
    },
    [setIsLoading, dispatch, creditPurposeFormData],
  );

  const onBlur: FocusEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const rawValue = event.target.value;
      const value = rawValue.replace(/\D/g, '');
      const isValidValue = value && !Number.isNaN(Number(value));
      if (!isValidValue) {
        return;
      }
      if (!isMobile) {
        updateCreditLimitFilter(value);
      }
    },
    [updateCreditLimitFilter, isMobile],
  );

  const onSubmit = useCallback(async () => {
    if (isMobile) {
      await updateCreditLimitFilter(creditPurposeFormData?.amount, onButtonClick);
    } else {
      onButtonClick?.(creditPurposeFormData?.amount);
    }
  }, [isMobile, updateCreditLimitFilter, onButtonClick, creditPurposeFormData?.amount]);

  return (
    <Card
      size={16}
      className={styles.filtersCard}
    >
      <Row gutter={[16, 16]}>
        <Col span={colSpanConfig}>
          <NumberInput
            allowedDecimalSeparators={[]}
            label={VitrinsCreditCardsFiltersFormTexts.LimitInputLabel}
            value={Number(creditPurposeFormData?.amount)}
            max={AMOUNTS.maxVitrinsCardLimit}
            min={AMOUNTS.minVitrinsCardLimit}
            onBlur={onBlur}
            thousandSeparator=" "
            postfix={CURRENCY_CHAR}
          />
        </Col>
        <Col span={colSpanConfig}>
          <Button
            className={styles.filtersButton}
            block={isMobile}
            variant="primary"
            type="button"
            size={isMobile ? 52 : 60}
            onClick={onSubmit}
            loading={isLoading}
          >
            {VitrinsCreditCardsFiltersFormTexts.ButtonLabel} {vitrinsOffersCount}
          </Button>
        </Col>
      </Row>
    </Card>
  );
};
